import { KIT_ANCHORS } from '@/components/subflow-v3/components/utils';

export const getPlayKitProductDescription = (description: string) => {
  if (!description) return '';

  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(description, 'text/html');
  const firstParagraph = htmlDoc.getElementsByTagName('P')[0]?.innerHTML;
  const removedBreaks = firstParagraph?.replace('<br>', '');

  return removedBreaks;
};

/**
 * Normalizes different anchor formats to the standard KIT_ANCHORS format
 * Handles:
 * - "the-persister-play-kit" (API format) -> "kit-the-persister"
 * - "the-play-kits-the-connector" (Shopify handle) -> "kit-the-connector"
 * - "kit-the-persister" (already normalized) -> "kit-the-persister"
 */
export const normalizeKitAnchor = (anchor?: string): string | undefined => {
  if (!anchor) return undefined;

  // Already in correct format
  if (anchor.startsWith('kit-the-')) {
    return anchor;
  }

  // Handle "the-play-kits-the-{name}" format (Shopify handle)
  if (anchor.startsWith('the-play-kits-the-')) {
    return anchor.replace('the-play-kits-the-', 'kit-the-');
  }

  // Handle "the-{name}-play-kit" format (API format)
  if (anchor.endsWith('-play-kit')) {
    return anchor.replace(/^the-(.+)-play-kit$/, 'kit-the-$1');
  }

  return anchor;
};

/**
 * Array of play kit handles that are English only in the EU region
 * Other play kits may be added to this array in the future, as needed.
 */
const ENGLISH_ONLY_PLAY_KIT_HANDLES_EU = [
  KIT_ANCHORS.CONNECTOR,
  KIT_ANCHORS.EXAMINER,
  KIT_ANCHORS.PERSISTER,
  KIT_ANCHORS.PLANNER,
];

/**
 * Checks if a play kit is English-only based on its handle and region
 * @param handle - The Shopify product handle to check
 * @param isEUStore - Whether the store is in the EU region
 * @returns true if the kit is English only, false if it supports multiple languages
 */
export const isEnglishOnlyPlayKit = (
  handle?: string,
  isEUStore = false,
): boolean => {
  if (!handle) return false;

  if (isEUStore) {
    // In EU, check if it's in the English-only list
    return ENGLISH_ONLY_PLAY_KIT_HANDLES_EU.includes(
      normalizeKitAnchor(handle) || '',
    );
  }

  // Outside EU, no kits are English-only
  return false;
};
